import React, { useEffect, useState } from "react";
import apiService from "../services/api";
import { CallPermission, PermissionStatus } from "../hooks/useWhatsAppCallPermission";

interface Props {
  patientId: number | string;
  phone?: string;
  token: string;
  /** Fetched by useWhatsAppCallPermission in the parent; null while loading. */
  permission: CallPermission | null;
  onPermissionChange: (permission: CallPermission) => void;
}

const STATUS_TEXT: Record<PermissionStatus, string> = {
  accepted: "Patient approved WhatsApp calls",
  requested: "Permission request already sent - waiting for the patient's approval",
  rejected: "Patient rejected WhatsApp calls",
  expired: "Call approval expired (older than 7 days)",
  unknown: "Patient hasn't approved WhatsApp calls yet",
};

/**
 * Shows the stored WhatsApp call-permission state for the number about to be
 * dialed, before the call is placed (calls to unapproved numbers fail with
 * Meta error 37000). Offers sending the permission request inline.
 *
 * The state itself is owned by the parent - the default call method depends on
 * it too, so it cannot live here.
 */
export const WhatsAppCallPermissionHint: React.FC<Props> = ({
  patientId,
  phone,
  token,
  permission,
  onPermissionChange,
}) => {
  const [sendState, setSendState] = useState<"idle" | "sending" | "sent" | "already_sent" | "failed">("idle");

  // A request sent for one number says nothing about another.
  useEffect(() => {
    setSendState("idle");
  }, [patientId, phone]);

  const handleSendRequest = async () => {
    setSendState("sending");
    try {
      const res = await apiService.requestWhatsAppCallPermission({
        token,
        patient_id: Number(patientId),
        phone: phone || undefined,
      });
      if (res?.already_approved) {
        onPermissionChange({ status: "accepted", valid_until: null, requested_at: null });
        setSendState("sent");
      } else if (res?.already_sent) {
        onPermissionChange({ status: "requested", valid_until: null, requested_at: permission?.requested_at ?? null });
        setSendState("already_sent");
      } else {
        onPermissionChange({ status: "requested", valid_until: null, requested_at: new Date().toISOString() });
        setSendState("sent");
      }
    } catch (error) {
      console.error("[WhatsAppCallPermissionHint] Permission request failed", error);
      setSendState("failed");
    }
  };

  if (!permission) return null; // still loading - don't flash a wrong state

  const { status, valid_until: validUntil, requested_at: requestedAt } = permission;
  const approved = status === "accepted";
  const pending = status === "requested";
  return (
    <div
      className={`rounded-lg px-3 py-2 text-xs ${
        approved ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"
      }`}
    >
      <p className="font-medium">
        {approved && <span className="mr-1">✓</span>}
        {STATUS_TEXT[status]}
        {approved && validUntil && (
          <span className="font-normal opacity-70"> (until {new Date(validUntil).toLocaleDateString()})</span>
        )}
        {pending && requestedAt && (
          <span className="font-normal opacity-70"> (sent {new Date(requestedAt).toLocaleString()})</span>
        )}
      </p>
      {!approved && (
        <>
          <p className="mt-1 opacity-80">
            WhatsApp calls only reach the patient after they approve calls from the clinic.
          </p>
          <button
            type="button"
            onClick={handleSendRequest}
            disabled={sendState === "sending" || sendState === "sent" || sendState === "already_sent"}
            className="mt-1.5 px-2.5 py-1 rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
          >
            {sendState === "sending"
              ? "Sending…"
              : sendState === "sent"
              ? "Permission request sent"
              : sendState === "already_sent"
              ? "Request already sent - waiting for the patient's reply"
              : sendState === "failed"
              ? "Failed - try again"
              : pending
              ? "Send request again"
              : "Send call permission request"}
          </button>
        </>
      )}
    </div>
  );
};

export default WhatsAppCallPermissionHint;
